import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Link,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { faHexagonExclamation } from '@fortawesome/pro-solid-svg-icons/faHexagonExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { formattedUserFacingDateTime, formattedUserFacingMonthDayYearShort } from 'DateHelpers';
import { theme } from 'FrontRoyalMaterialUiForm';
import { SUBMISSION_FORMAT_LABELS } from 'LearnerProjects';
import { RenderMarkdown } from 'MarkdownComponents';
import { createOutsideAdminProjectSubmissionModal } from 'ProjectSubmission';
import transformKeyCase from 'Utils/transformKeyCase';
import moment, { type Moment } from 'moment-timezone';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { AutogradeRegenerateButton } from './AutogradeRegenerateButton';
import { applyUploadedProjectProgress, buildSubmissionData } from './buildProjectFieldsModalProps';
import { notifyToast } from './notifyToast';
import { ProjectChangeHistory } from './ProjectChangeHistory';
import {
    type IguanaProjectProgressInput,
    type ProjectFieldsModalProps,
    type ProjectFieldsModalSubmissionData,
    type SavedProjectProgress,
} from './ProjectFieldsModal.types';
import {
    hasScoreSet,
    idVerificationGate,
    isApproveDisabled,
    provisionalScoreOptionValue,
    scoreOptionValue,
} from './projectFieldsLogic';
import {
    ID_VERIFIED_OPTIONS,
    SIMILARITY_AND_ORIGINALITY_STATUS_OPTIONS,
    buildScoreOptions,
    buildStatusOptions,
    buildVariantOptions,
    formatSimilarityAndOriginalityStatus,
    isSimilarityStatusClearedForGrading,
} from './projectFieldsOptions';
import { SelectField } from './SelectField';
import { useProjectFieldsForm } from './useProjectFieldsForm';

// The original modal sat at z-index 10001, above a HeroUI modal at z-[10000]. The autograding prompt
// builder opens it that way. Raise the MUI modal layer to keep that order. The Dialog, the Select
// menus and the date-picker popper all read zIndex.modal, so the selects stay above the dialog. The
// HeroUI upload modal at z-[10003] stays above all of it.
const modalTheme = createTheme(theme, { zIndex: { modal: 10001 } });

// SUBMISSION_FORMAT_LABELS uses SubmissionFormatType keys, but the API sends plain strings. Look up
// through a string index, and fall back when the type is unknown.
const formatLabel = (type: string | null | undefined, fallback: string) =>
    (SUBMISSION_FORMAT_LABELS as Record<string, string>)[type ?? ''] || fallback;

type Form = ReturnType<typeof useProjectFieldsForm>;
// `showSimilarity` is shared. The toggle below the S&O select also reveals the S&O status in the
// submissions list.
type SectionProps = {
    form: Form;
    props: ProjectFieldsModalProps;
    showSimilarity: boolean;
    toggleSimilarity: () => void;
    onSubmissionUploaded: (uploaded: IguanaProjectProgressInput) => void;
};

function ExternalLink({ url, children }: { url: string; children: React.ReactNode }) {
    return (
        <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
            {children}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
    );
}

function SubmittedBy({ props }: SectionProps) {
    const [open, setOpen] = useState(false);
    const members = props.submissionData.projectGroupMembers.length
        ? props.submissionData.projectGroupMembers
        : [props.user];
    const submitterId = props.submissionData.projectSubmission?.user_id;

    return (
        <Box>
            <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setOpen(!open)}
                sx={{ color: 'text.primary' }}
            >
                <strong>Submitted {members.length > 1 ? 'For' : 'By'}: </strong>
                {members
                    .map(m => m.name)
                    .filter(Boolean)
                    .join(', ')}
                {open ? (
                    <ExpandLessIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', ml: 0.5 }} />
                ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', ml: 0.5 }} />
                )}
            </Link>
            {open && (
                <Stack component="ul" sx={{ listStyle: 'none', pl: 0, mt: 1 }} spacing={0.5}>
                    {members.map(member => (
                        <li key={member.id}>
                            <Link href={`/admin/users?user-id=${member.id}`}>{member.name}</Link> · {member.email}
                            {submitterId === member.id && <strong> (Submitter)</strong>}
                        </li>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

// A hexagon-exclamation inside a circle, in the banner color, with a soft shadow, as the mockup
// shows. The `!` cutout shows the circle color, which looks near-white against the icon.
function BannerHexIcon({ circleColor, hexColor }: { circleColor: string; hexColor: string }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: '50%',
                backgroundColor: circleColor,
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
            }}
        >
            <FontAwesomeIcon icon={faHexagonExclamation} style={{ color: hexColor, fontSize: 18 }} />
        </Box>
    );
}

function GradingBanners({ form, props }: SectionProps) {
    const gradedAt = props.submissionData.projectGradedEventLastLoggedAt;
    const multiple = props.submissionData.projectGroupMembers.length > 1;
    const recipient = multiple ? 'group' : 'student';
    const provisionalScoreSet = form.isProvisionalNow && form.fields.provisionalScore != null;
    // The "previously approved" case. The load had no approved score, but a saved provisional one.
    const hadSavedProvisionalScore =
        scoreOptionValue(form.originalFields) === null && provisionalScoreOptionValue(form.originalFields) !== null;
    const approvingVisibilityNote = multiple
        ? ' Approving will make the score and feedback visible to all group members.'
        : '';
    // M/D/YY, with useDateThreshold false. This matches the original banner's date helper.
    const gradedDate = gradedAt ? formattedUserFacingMonthDayYearShort(new Date(gradedAt * 1000), false) : '';
    const skippedFormatTypes = props.submissionData.projectSubmissionAutogradingSkippedFormatTypes;
    const skippedFormatLabels = skippedFormatTypes.map(type => formatLabel(type, type)).join(', ');

    return (
        <>
            {provisionalScoreSet && !gradedAt && (
                <Alert
                    icon={<BannerHexIcon circleColor="#FFE2E4" hexColor="#ED1137" />}
                    sx={{
                        bgcolor: '#FFE2E4',
                        color: '#ED1137',
                        alignItems: 'center',
                        '& .MuiAlert-icon': { opacity: 1 },
                        '& .MuiAlert-message': { fontWeight: 600 },
                    }}
                >
                    The score and feedback below have not been marked as approved.{approvingVisibilityNote}
                </Alert>
            )}
            {provisionalScoreSet && skippedFormatTypes.length > 0 && (
                <Alert
                    icon={<BannerHexIcon circleColor="#FFFBC5" hexColor="#E29200" />}
                    sx={{
                        bgcolor: '#FFFBC5',
                        color: '#7C410B',
                        alignItems: 'center',
                        '& .MuiAlert-icon': { opacity: 1 },
                        '& .MuiAlert-message': { fontWeight: 600 },
                    }}
                >
                    The auto-grader graded only part of this submission. It did not read: {skippedFormatLabels}. Review
                    those parts by hand before approving the score and feedback.
                </Alert>
            )}
            {hadSavedProvisionalScore && gradedAt && (
                <Alert
                    icon={<BannerHexIcon circleColor="#FFFBC5" hexColor="#E29200" />}
                    sx={{
                        bgcolor: '#FFFBC5',
                        color: '#7C410B',
                        alignItems: 'center',
                        '& .MuiAlert-icon': { opacity: 1 },
                        '& .MuiAlert-message': { fontWeight: 600 },
                    }}
                >
                    Previous score and feedback were approved and sent to the {recipient} on {gradedDate}. The score and
                    feedback below have not been marked as approved.{approvingVisibilityNote}
                </Alert>
            )}
            {gradedAt && hasScoreSet(form.fields) && (
                <Alert
                    icon={<CheckCircleIcon />}
                    sx={{
                        bgcolor: '#D8FFE0',
                        color: '#0C5B1F',
                        alignItems: 'center',
                        '& .MuiAlert-icon': { color: '#04B52A' },
                        '& .MuiAlert-message': { fontWeight: 600 },
                    }}
                >
                    Score and feedback were approved and sent to the {recipient} on {gradedDate}.
                </Alert>
            )}
        </>
    );
}

function SimilarityAndVariant({ form, props, showSimilarity, toggleSimilarity }: SectionProps) {
    const { project } = props;
    const hasSubmission = !!props.submissionData.projectSubmission;

    return (
        <>
            {(project.similarityAndOriginalityCheckRequired || showSimilarity) && (
                <SelectField
                    label="Similarity & Originality Status"
                    options={[{ value: null, label: '' }, ...SIMILARITY_AND_ORIGINALITY_STATUS_OPTIONS]}
                    value={form.fields.projectSubmissionSimilarityAndOriginalityStatus}
                    onChange={v => form.setField('projectSubmissionSimilarityAndOriginalityStatus', v)}
                    disabled={!hasSubmission}
                    helperText={hasSubmission ? undefined : 'Status can be updated once a submission is on file.'}
                />
            )}
            {!project.similarityAndOriginalityCheckRequired && (
                <Link component="button" type="button" onClick={toggleSimilarity} sx={{ alignSelf: 'flex-start' }}>
                    Similarity and Originality check not required ({showSimilarity ? 'hide' : 'show'} status)
                </Link>
            )}
            {project.variants.length > 0 && (
                <SelectField
                    label="Project Variant"
                    options={buildVariantOptions(project.variants, form.fields.projectSubmissionSelectedProjectVariant)}
                    value={form.fields.projectSubmissionSelectedProjectVariant}
                    onChange={v => form.setField('projectSubmissionSelectedProjectVariant', v)}
                    disabled={!hasSubmission}
                    helperText={hasSubmission ? undefined : 'Variant can be updated once a submission is on file.'}
                />
            )}
        </>
    );
}

function ScoreAndIdentity({ form, props }: SectionProps) {
    const { project, cohort } = props;
    const scoreValue = form.originalIsProvisional
        ? provisionalScoreOptionValue(form.fields)
        : scoreOptionValue(form.fields);

    return (
        <Stack direction="row" spacing={2}>
            <SelectField
                label="Score"
                options={buildScoreOptions(project.passingScore, cohort.isExecEd)}
                value={scoreValue}
                onChange={form.setScoreOption}
            />
            {project.isPresentationProject && (
                <SelectField
                    label="Identity Verification"
                    options={[...ID_VERIFIED_OPTIONS]}
                    value={form.fields.idVerified}
                    onChange={v => form.setField('idVerified', v)}
                />
            )}
        </Stack>
    );
}

function FeedbackField({ form, props }: SectionProps) {
    const { project } = props;
    const provisional = form.isProvisionalNow;
    const fieldKey = provisional ? 'provisionalFeedback' : 'feedback';
    const content = (provisional ? form.fields.provisionalFeedback : form.fields.feedback) ?? '';
    const [editing, setEditing] = useState(!content);

    const variant = project.variants.find(v => v.slug === form.fields.projectSubmissionSelectedProjectVariant);
    const template = variant?.feedbackTemplate || project.feedbackTemplate || '';

    return (
        <Box>
            {/* The mockup omits a "Feedback" header; the textarea's placeholder conveys the field. */}
            {template && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <Link
                        component="button"
                        type="button"
                        disabled={!!content.trim()}
                        onClick={() => {
                            form.setField(fieldKey, template);
                            setEditing(true);
                        }}
                    >
                        Insert Header Template…
                    </Link>
                </Box>
            )}
            {!editing && content.trim() ? (
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditing(true)}
                    onKeyDown={e => e.key === 'Enter' && setEditing(true)}
                    sx={{ cursor: 'text', minHeight: 160, bgcolor: 'grey.100', borderRadius: 1, p: 1.5 }}
                >
                    <RenderMarkdown>{content}</RenderMarkdown>
                </Box>
            ) : (
                <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    placeholder="Enter your feedback here (Markdown formatting)"
                    value={content}
                    onChange={e => form.setField(fieldKey, e.target.value)}
                    onBlur={() => content.trim() && setEditing(false)}
                />
            )}
        </Box>
    );
}

function ResubmissionSection({ form }: SectionProps) {
    const { fields } = form;
    if (fields.waived) return null;
    const dueDate = fields.resubmissionDueDate != null ? moment.unix(fields.resubmissionDueDate) : null;

    return (
        <Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={fields.allowResubmission}
                        disabled={fields.resubmissionRequired && fields.allowResubmission}
                        onChange={e => form.setAllowResubmission(e.target.checked)}
                    />
                }
                label="Allow Resubmission"
            />
            {fields.resubmissionRequired && fields.allowResubmission && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    Automatically enabled because Resubmission Required is selected.
                </Typography>
            )}
            {fields.allowResubmission && (
                <DatePicker
                    label="Resubmission Due Date"
                    value={dueDate}
                    // A picker merges the picked day into the existing value and keeps its time. The old
                    // admin-datetimepicker produced the start of the day, so truncate to match.
                    onChange={(m: Moment | null) =>
                        form.setField(
                            'resubmissionDueDate',
                            m && m.isValid() ? Math.floor(m.clone().startOf('day').valueOf() / 1000) : null,
                        )
                    }
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
            )}
        </Box>
    );
}

function AdminReviewSection({ form, props }: SectionProps) {
    if (!props.submissionData.projectSubmission) return null;
    const { isAdmin } = props;
    const needsReview = form.fields.projectSubmissionNeedsAdminReview;
    // A non-admin cannot unflag a submission that was flagged at load. Compare the saved baseline.
    const disabled = !isAdmin && form.originalFields.projectSubmissionNeedsAdminReview;

    let helpText: string | null = null;
    if (needsReview && isAdmin) {
        helpText = 'This submission is flagged for admin review. Uncheck and save to return to the grader.';
    } else if (needsReview && !disabled) {
        helpText = 'This submission will be flagged for admin review. Add private notes below to provide context.';
    } else if (needsReview && disabled) {
        helpText = 'This submission has been flagged for admin review.';
    }

    // Wrap this in a Box. The parent Stack would otherwise reset the negative left margin on MUI's
    // FormControlLabel, and the checkbox would fall out of line with the others.
    return (
        <Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={needsReview}
                        disabled={disabled}
                        onChange={e => form.setField('projectSubmissionNeedsAdminReview', e.target.checked)}
                    />
                }
                label={disabled ? 'Sent to Admin for Review' : 'Flag for Admin Review'}
            />
            {helpText && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    {helpText}
                </Typography>
            )}
        </Box>
    );
}

// Formats a presentation video length, in milliseconds, as "Xm Ys" next to the transcript label.
function formatPresentationDuration(ms: number | null): string | null {
    if (!ms) return null;
    const totalSeconds = Math.round(ms / 1000);
    return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
}

function PrivateNotesAndTranscript({ form, props }: SectionProps) {
    const { project, submissionData } = props;
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    if (!submissionData.projectSubmission) return null;
    const transcript = form.fields.projectSubmissionPresentationTranscript;
    const duration = formatPresentationDuration(submissionData.projectSubmissionPresentationDurationMs);

    return (
        <>
            <TextField
                label="Private Notes (visible to grading portal users)"
                fullWidth
                multiline
                minRows={3}
                value={form.fields.projectSubmissionPrivateNotes ?? ''}
                onChange={e => form.setField('projectSubmissionPrivateNotes', e.target.value)}
            />
            {project.hasPresentationLinkFormat && (
                <Box>
                    {/* Collapsed by default, matching the original. */}
                    <Link
                        component="button"
                        type="button"
                        onClick={() => setTranscriptOpen(!transcriptOpen)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.primary' }}
                    >
                        Presentation Transcript
                        {duration && (
                            <Typography component="span" variant="caption" color="text.secondary">
                                ({duration})
                            </Typography>
                        )}
                        {!transcript && (
                            <Typography component="span" variant="caption" color="text.secondary">
                                (none)
                            </Typography>
                        )}
                        {transcriptOpen ? (
                            <ExpandLessIcon sx={{ fontSize: 18 }} />
                        ) : (
                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                        )}
                    </Link>
                    {transcriptOpen && (
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={6}
                                placeholder="Paste the presentation transcript here..."
                                value={transcript ?? ''}
                                onChange={e => form.setField('projectSubmissionPresentationTranscript', e.target.value)}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                The transcript is used for AI-assisted grading of presentation submissions.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
}

function SaveButtons({ form, props }: SectionProps) {
    const disabled = !form.hasChanges || form.isSaving;
    // "Save and Approve" also works with no other edit, when an unapproved provisional score is
    // ready to approve as it stands. The original enabled Save the same way, through the approve
    // checkbox. A presentation project adds the ID Verified gate (#17035). An unset value blocks the
    // save. An unverified ID routes to admin review instead of approving.
    const approveGate = idVerificationGate(form.fields, props.project.isPresentationProject, true);
    const approveDisabled = isApproveDisabled(form.fields, approveGate, form.isSaving);
    // A group submission shows the approved score and feedback to every group member. Any other
    // submission shows them to the one student. This matches the original approval note.
    const approvalRecipient = props.submissionData.projectGroupMembers.length > 1 ? 'all group members' : 'the student';

    return (
        <Box>
            {form.isProvisionalNow && approveGate === 'blank' && (
                <Typography variant="body2" sx={{ color: '#ED1137', mb: 1 }}>
                    <FontAwesomeIcon icon={faHexagonExclamation} style={{ marginInlineEnd: 6 }} />
                    Select &quot;ID verified&quot; or &quot;ID NOT verified&quot; before approving the score and
                    feedback.
                </Typography>
            )}
            {form.isProvisionalNow && approveGate === 'auto_flag' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    “Save and Approve” will flag this submission for admin review instead of approving it, because ID is
                    NOT verified. The provisional score and feedback will be preserved for the admin to review.
                </Typography>
            )}
            {form.isProvisionalNow ? (
                <Stack direction="row" spacing={2}>
                    {/* "Save" saves every field and leaves the grade provisional. It matches the plain
                        Save in weight, so the green button does not pull graders toward approval. */}
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={disabled || form.fields.resubmissionRequired}
                        onClick={() => form.save(false)}
                    >
                        Save
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={approveDisabled}
                        onClick={() => form.save(true)}
                        sx={{ bgcolor: '#0DD037', '&:hover': { bgcolor: '#04B52A' } }}
                    >
                        Save and Approve
                    </Button>
                </Stack>
            ) : (
                <Button fullWidth variant="contained" disabled={disabled} onClick={() => form.save(false)}>
                    {form.isSaving ? 'Saving…' : 'Save'}
                </Button>
            )}
            {form.isProvisionalNow && (approveGate === 'na' || approveGate === 'ok') && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                >
                    “Save and Approve” will make the score and feedback visible to {approvalRecipient}.
                </Typography>
            )}
            {form.saveError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {form.saveError}
                </Alert>
            )}
        </Box>
    );
}

function SubmissionsList({ props, showSimilarity, onSubmissionUploaded }: SectionProps) {
    const { submissionData, project, user, cohort } = props;
    const submissions = [...submissionData.historicalProjectSubmissions].sort((a, b) => b.created_at - a.created_at);
    const showSimilarityStatus = project.similarityAndOriginalityCheckRequired || showSimilarity;

    const openUpload = () =>
        createOutsideAdminProjectSubmissionModal(
            props.uploadProject,
            { id: user.id, name: user.name, email: user.email },
            cohort.id,
            props.projectGroupMembership,
            response => {
                if (!response.meta.projectProgress) return;
                // RTK Query camelCases the response. The adapter and the Iguana consumers, such as
                // the gradebook onSaved, both need snake_case. Convert it back, as the original did.
                const uploaded = transformKeyCase(response.meta.projectProgress, {
                    to: 'snakeCase',
                }) as unknown as IguanaProjectProgressInput;
                // Resync the open modal in place, refresh the parent, and notify. The parent is the
                // gradebook cell or the Grading Portal.
                onSubmissionUploaded(uploaded);
                props.onSaved(uploaded as unknown as SavedProjectProgress);
                notifyToast('Submission uploaded successfully');
            },
        );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">Project Submissions</Typography>
                {props.isAdmin && (
                    <Button size="small" variant="contained" onClick={openUpload}>
                        Upload new submission
                    </Button>
                )}
            </Box>
            {submissions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No project submissions found.
                </Typography>
            ) : (
                <Stack component="ul" sx={{ listStyle: 'none', pl: 0 }} spacing={1.5}>
                    {submissions.map((submission, index) => {
                        const soStatus = submission.similarity_and_originality_status ?? null;
                        const soClear = isSimilarityStatusClearedForGrading(soStatus);
                        return (
                            <li key={submission.id}>
                                <Box
                                    sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Submission #{submissions.length - index}
                                        {index === 0 ? ' (current submission)' : ''}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Submitted at{' '}
                                        {formattedUserFacingDateTime(new Date(submission.created_at * 1000))}
                                    </Typography>
                                </Box>
                                <Stack component="ul" sx={{ listStyle: 'none', pl: 0 }}>
                                    {(submission.project_submission_attachments || []).map(attachment => (
                                        <li key={attachment.url}>
                                            <ExternalLink url={attachment.url}>
                                                {formatLabel(attachment.submission_format_type, 'File')}
                                            </ExternalLink>
                                        </li>
                                    ))}
                                </Stack>
                                {submission.group_project_agreement_google_drive_file_url && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <ExternalLink url={submission.group_project_agreement_google_drive_file_url}>
                                            View group project agreement
                                        </ExternalLink>
                                    </Box>
                                )}
                                {showSimilarityStatus && (
                                    <Typography variant="body2" color="text.secondary">
                                        Similarity &amp; Originality:{' '}
                                        <Box
                                            component="span"
                                            sx={{
                                                color: soClear ? 'text.secondary' : '#ED1137',
                                                fontWeight: soClear ? 400 : 600,
                                            }}
                                        >
                                            {formatSimilarityAndOriginalityStatus(soStatus) || 'Not set'}
                                        </Box>
                                    </Typography>
                                )}
                            </li>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}

function PromptDocuments({ props }: SectionProps) {
    const documents = props.project.projectDocuments;
    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Project Prompt Documents
            </Typography>
            {documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No project prompt documents found.
                </Typography>
            ) : (
                <Stack component="ul" sx={{ listStyle: 'none', pl: 0 }}>
                    {documents.map(document => (
                        <li key={document.url}>
                            <ExternalLink url={document.url}>{document.title || document.fileFileName}</ExternalLink>
                        </li>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

// Runs after a regrade completes. It applies the AI provisional score and feedback to the open form.
// Then it refreshes the parent and the Change History. The parent is the gradebook cell or the
// Grading Portal cache. The original did the same through onAutogradingCompleted and onSuccess.
//
// It takes `notifyParentSaved`, not `handleSaved`, because its payload is a synthetic two-field
// patch, not a server record. The handler holds a stable reference, because it depends on the
// memoized applyAutogradeResult and not on the whole form. So it never restarts the poll.
// `progressId` always has a value here, because a regrade needs a submission with saved progress.
function useAutogradeCompletedHandler(
    applyAutogradeResult: (
        gradedScore: number,
        gradedFeedback: string | null,
        presentationTranscript: string | null,
    ) => void,
    progressId: string | null | undefined,
    notifyParentSaved: (saved: SavedProjectProgress) => void,
    setSubmissionData: Dispatch<SetStateAction<ProjectFieldsModalSubmissionData>>,
) {
    return useCallback(
        (
            gradedScore: number,
            gradedFeedback: string | null,
            presentationTranscript: string | null,
            skippedFormatTypes: string[],
        ) => {
            applyAutogradeResult(gradedScore, gradedFeedback, presentationTranscript);
            setSubmissionData(prev => ({
                ...prev,
                projectSubmissionAutogradingSkippedFormatTypes: skippedFormatTypes,
            }));
            if (progressId) {
                notifyParentSaved({
                    id: progressId,
                    provisional_score: gradedScore,
                    provisional_feedback: gradedFeedback,
                } as unknown as SavedProjectProgress);
            }
        },
        [applyAutogradeResult, progressId, notifyParentSaved, setSubmissionData],
    );
}

// Owns the modal's live state and the two save callbacks.
//
// submissionData and initialFields sit in state, so a new admin upload can resync the open modal in
// place. The original directive also patched its record and stayed open.
//
// `identity` sits in state, so a new ungraded project progress picks up its server id after the
// first save. Such a record loads with a null id. The original bound straight to the Iguana record,
// which filled its id in place. Here the save response carries the id back. Change History keys on
// identity.id and so loads in session, and a later save sends the right id.
//
// timelineNonce bumps on every save and upload, so the Change History refetches. The original
// watched `updated_at`.
function useModalState(props: ProjectFieldsModalProps) {
    const { onSaved } = props;
    const [submissionData, setSubmissionData] = useState(props.submissionData);
    const [initialFields, setInitialFields] = useState(props.initialFields);
    const [identity, setIdentity] = useState(props.identity);
    const [timelineNonce, setTimelineNonce] = useState(0);

    // Promotes a server-assigned id, refetches the Change History, and forwards to the parent.
    const notifyParentSaved = useCallback(
        (saved: SavedProjectProgress) => {
            if (saved?.id && saved.id !== identity.id) {
                setIdentity(prev => ({ ...prev, id: saved.id }));
            }
            setTimelineNonce(nonce => nonce + 1);
            onSaved(saved);
        },
        [onSaved, identity.id],
    );

    // A save returns the full cohort_gradebook record, so rebuild the read-only submission data from
    // it. The old Iguana save() merged the response into the record. Without this step the approval
    // banners keep their pre-save values. The server sets project_graded_event_last_logged_at, the
    // modal still reads null, and the green "approved and sent" banner never appears.
    //
    // The editable fields keep their client-side baseline on purpose. A new baseline would add
    // nothing, and the reducer already owns that state.
    const handleSaved = useCallback(
        (saved: SavedProjectProgress) => {
            notifyParentSaved(saved);
            setSubmissionData(buildSubmissionData(saved as unknown as IguanaProjectProgressInput));
        },
        [notifyParentSaved],
    );

    // Resyncs the open modal from an admin upload response.
    const onSubmissionUploaded = useCallback((uploaded: IguanaProjectProgressInput) => {
        const next = applyUploadedProjectProgress(uploaded);
        setInitialFields(next.initialFields);
        setSubmissionData(next.submissionData);
        setTimelineNonce(nonce => nonce + 1);
    }, []);

    return {
        submissionData,
        setSubmissionData,
        initialFields,
        identity,
        timelineNonce,
        notifyParentSaved,
        handleSaved,
        onSubmissionUploaded,
    };
}

export function ProjectFieldsModal(props: ProjectFieldsModalProps) {
    const { project, closeModal } = props;
    const {
        submissionData,
        setSubmissionData,
        initialFields,
        identity,
        timelineNonce,
        notifyParentSaved,
        handleSaved,
        onSubmissionUploaded,
    } = useModalState(props);
    const liveProps = useMemo<ProjectFieldsModalProps>(
        () => ({ ...props, identity, submissionData, initialFields, onSaved: handleSaved }),
        [props, identity, submissionData, initialFields, handleSaved],
    );
    const form = useProjectFieldsForm(liveProps);
    const [showSimilarity, setShowSimilarity] = useState(false);

    const handleAutogradeCompleted = useAutogradeCompletedHandler(
        form.applyAutogradeResult,
        identity.id,
        notifyParentSaved,
        setSubmissionData,
    );

    const sectionProps: SectionProps = {
        form,
        props: liveProps,
        showSimilarity,
        toggleSimilarity: () => setShowSimilarity(value => !value),
        onSubmissionUploaded,
    };
    // No blank row. The server rejects a null status on a non-waived record, so the row would only
    // produce a validation error. SelectField renders an empty control when no option matches, which
    // is what an unset status shows. The original ng-options select behaved the same way.
    const statusOptions = buildStatusOptions(project.validStatusOptions, form.fields.status);

    return (
        <ThemeProvider theme={modalTheme}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Dialog
                    open
                    // The original opened through DialogModal.alert, which closed only on the X button.
                    // A backdrop click or Escape would discard unsaved score, feedback and notes.
                    onClose={(_event, reason) => {
                        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') closeModal();
                    }}
                    // The upload modal renders in its own React root outside this dialog. MUI's focus
                    // trap pulls focus back on every focusin, so its inputs drop every keystroke.
                    disableEnforceFocus
                    maxWidth="sm"
                    fullWidth
                    scroll="paper"
                    className="select-text"
                >
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {project.title}
                        <IconButton onClick={closeModal} aria-label="Close" size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2.5}>
                            <SubmittedBy {...sectionProps} />
                            <GradingBanners {...sectionProps} />
                            <SimilarityAndVariant {...sectionProps} />
                            <ScoreAndIdentity {...sectionProps} />
                            <FeedbackField {...sectionProps} />
                            <AutogradeRegenerateButton
                                submissionId={submissionData.projectSubmission?.id ?? null}
                                autoGradingEnabled={project.autoGradingEnabled}
                                hasSubmission={!!submissionData.projectSubmission}
                                hasScoreSet={hasScoreSet(form.fields)}
                                serverStatus={submissionData.projectSubmissionAutogradingStatus}
                                onCompleted={handleAutogradeCompleted}
                                onStarted={form.clearProvisionalGrade}
                            />
                            <SelectField
                                label="Status"
                                options={statusOptions}
                                value={form.fields.status}
                                onChange={v => form.setField('status', v)}
                            />
                            <ResubmissionSection {...sectionProps} />
                            <AdminReviewSection {...sectionProps} />
                            <SaveButtons {...sectionProps} />
                            <PrivateNotesAndTranscript {...sectionProps} />
                            <SubmissionsList {...sectionProps} />
                            <PromptDocuments {...sectionProps} />
                            <ProjectChangeHistory
                                projectProgressId={identity.id ?? null}
                                updatedAt={timelineNonce}
                                historicalSubmissions={submissionData.historicalProjectSubmissions}
                            />
                        </Stack>
                    </DialogContent>
                </Dialog>
            </LocalizationProvider>
        </ThemeProvider>
    );
}
